import { apiGet, apiPost } from "../core/request";

/**
 * Small compatibility guard: supports either { data: T } or raw T responses.
 */
function unwrap<T = any>(res: any): T {
  return (res && typeof res === "object" && "data" in res) ? (res.data as T) : (res as T);
}

export type FundingRequestDto = {
  id: number;
  user_id: number;
  account_id: number;
  currency: string;
  amount: number | null;
  method: "SEPA" | "SWIFT";
  reference: string;
  status: "pending" | "received" | "approved" | "rejected" | "cancelled";
  pof_required?: 0|1;
  sow_required?: 0|1;
  pof_status?: "pending"|"approved"|"rejected"|null;
  sow_status?: "pending"|"approved"|"rejected"|null;
  created_at: string;
  updated_at: string;
};

export type BankConfig = {
  provider_name?: string | null;
  bank_name?: string | null;
  bank_address?: string | null;
  bank_country?: string | null;
  iban?: string | null;
  bic?: string | null;
  intermediary_bank_name?: string | null;
  intermediary_bank_bic?: string | null;
  intermediary_bank_address?: string | null;
  currency?: string | null;
};

export async function apiFundingIndex() {
  const res = await apiGet("/xapi/funding");
  return unwrap(res);
}

export async function apiFundingCreatePrefetch() {
  const res = await apiGet("/xapi/funding/create");
  return unwrap(res);
}

export async function apiFundingStore(payload: {
  account_id: number;
  currency: string; // "EUR"
  method: "SEPA" | "SWIFT";
  amount: number;
}) {
  const res = await apiPost("/xapi/funding", payload);
  return unwrap<{
    funding: FundingRequestDto;
    requiresDocs: boolean;
    next: string | null;
  }>(res);
}

export async function apiFundingShow(fundingId: number) {
  const res = await apiGet(`/xapi/funding/${fundingId}`);
  return unwrap(res);
}

export async function apiFundingBankConfig(currency: string) {
  const q = new URLSearchParams({ currency: currency || "EUR" });
  const res = await apiGet(`/xapi/funding/bank-config?${q.toString()}`);
  return unwrap<{ bankConfig: BankConfig; selectedCurrency: string; userCurrencies: string[] }>(res);
}

export async function apiFundingDocsIndex(fundingId: number) {
  const res = await apiGet(`/xapi/funding/${fundingId}/docs`);
  return unwrap(res);
}

export async function apiFundingDocsUpload(fundingId: number, files: { pof_document?: File; sow_document?: File; }) {
  const form = new FormData();
  if (files.pof_document) form.append("pof_document", files.pof_document);
  if (files.sow_document) form.append("sow_document", files.sow_document);
  const res = await apiPost(`/xapi/funding/${fundingId}/docs`, form);
  return unwrap(res);
}

export async function apiFundingCancel(fundingId: number) {
  const res = await apiPost(`/xapi/funding/${fundingId}/cancel`, {});
  return unwrap(res);
}
